import React, { useEffect, useState, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Link, useNavigate, Redirect } from "react-router-dom"
import CreateCodLeadModal from "./CreateCodLead"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Form,
  InputGroup,
  InputGroupText,
  FormGroup,
  Label,
  UncontrolledTooltip,
  Modal,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Spinner,
} from "reactstrap"
import Select from "react-select"
import classnames from "classnames"
import { connect } from "react-redux"
import withRouter from "components/Common/withRouter"

import TableContainer from "../../components/Common/TableContainer"

// import img2 from "../../assets/images/no-time-off.59f54e64.svg"

//Date Picker
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "assets/scss/datatables.scss"

// import { cryptoOrderData } from "../../../common/data";

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"
import { getCryptoOrders } from "store/crypto/actions"

import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { post } from "helpers/api_helper"
import axios from "axios"
import DeleteModal from "components/Common/DeleteModal"
import apiURl from "url"
import ExcelDownload from "components/Common/ExcelDownload"

const AllBusinessCodLeads = () => {
  document.title = "ECS COD Leads"
  const navigate = useNavigate()

  // state:
  const [createOpen, setCreateOpen] = useState(false)
  const openCreate = () => setCreateOpen(true)
  const closeCreate = () => setCreateOpen(false)

  // ---- Role gating (optional: allow only admin/subadmin/superadmin) ----
  const role_ = (localStorage.getItem("role") || "").toLowerCase()
  useEffect(() => {
    if (!["admin", "subadmin", "superadmin"].includes(role_)) {
      // If you want everyone to access and let backend filter by city,
      // remove this redirect.
      navigate("/")
    }
  }, [role_, navigate])

  // ---- State ----
  const [codOrders, setCodOrders] = useState([]) // raw rows from API
  const [businesses, setBusinesses] = useState([]) // raw businesses
  const [isLoading, setIsLoading] = useState(false)

  // Filters UI state
  const [cityFilter, setCityFilter] = useState("")
  const [businessFilter, setBusinessFilter] = useState("") // business ID
  const [fromDate, setFromDate] = useState("") // yyyy-mm-dd
  const [toDate, setToDate] = useState("") // yyyy-mm-dd

  // Modal state
  const [modal, setModal] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)

  const ecsUserId = localStorage.getItem("login_id") // IMPORTANT: used by backend auth/filtering

  // ---- Helpers ----
  const formatCurrency = v =>
    v == null || v === "" ? "" : Number(v).toLocaleString()
  const formatDateTime = d => (d ? new Date(d).toLocaleString() : "")

  const normalized = s => (s || "").toString().trim().toLowerCase()

  const inDateRange = (isoString, from, to) => {
    if (!isoString) return false
    const d = new Date(isoString)

    // Normalize bounds to local timezone: [from 00:00:00, to 23:59:59]
    let ok = true
    if (from) {
      const f = new Date(from)
      f.setHours(0, 0, 0, 0)
      ok = ok && d >= f
    }
    if (to) {
      const t = new Date(to)
      t.setHours(23, 59, 59, 999)
      ok = ok && d <= t
    }
    return ok
  }

  // ---- Fetch COD leads ----
  const getAllCodLeads = async () => {
    if (!ecsUserId) {
      console.error("ecsUserId missing in localStorage")
      return
    }
    try {
      setIsLoading(true)
      const { data: res } = await axios.get(`${apiURl}/all-cod-leads`, {
        params: { ecsUserId },
        headers: { Accept: "application/json" },
      })
      if (res.status === "success") {
        setCodOrders(res.data || [])
      } else {
        console.error("Failed to fetch COD leads:", res.message)
      }
    } catch (error) {
      console.error("Error fetching COD leads:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // ---- Fetch Businesses (then keep only Approved) ----
  const getAllBusinesses = async () => {
    try {
      setIsLoading(true)
      const { data: res } = await axios.get(`${apiURl}/getAllBusinesses`, {
        headers: { Accept: "application/json" },
      })
      if (res.status === "success") {
        setBusinesses(res.businesses || [])
      } else {
        console.error("Failed to fetch businesses:", res.message)
      }
    } catch (error) {
      console.error("Error fetching businesses:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Approved businesses (case-insensitive "Approved")
  const approvedBusinesses = useMemo(
    () =>
      (businesses || []).filter(
        b => normalized(b?.accountStatus) === "approved"
      ),
    [businesses]
  )

  // helper if you don't already have it
  const val = (...xs) => xs.find(x => x !== undefined && x !== null && x !== "")

  // Unique city suggestions from the leads themselves (Business City)
  const citySuggestions = useMemo(() => {
    const set = new Set(
      (codOrders || [])
        .map(r => val(r.business?.city, r.business_city, r.abr_business_city))
        .filter(Boolean)
        .map(s => String(s).trim())
    )
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [codOrders])

  // ---- Apply filters (individually AND collectively) on the client ----
  const filteredRows = useMemo(() => {
    const cityN = normalized(cityFilter)

    return (codOrders || []).filter(row => {
      // City filter: case-insensitive partial match on BUSINESS city
      const rowCity = normalized(
        val(row.business?.city, row.business_city, row.abr_business_city)
      )
      const passCity = !cityN || rowCity.includes(cityN)

      // Business filter: exact match on business ID
      const rowBizId = val(row.business?.id, row.business_id, row.abr_id)
      const passBusiness =
        !businessFilter || String(rowBizId) === String(businessFilter)

      // Date range filter: check createdAt (with aliases)
      const createdAt = val(
        row.lead_created_at,
        row.lead_createdAt,
        row.createdAt
      )
      const passDate =
        (!fromDate && !toDate) || inDateRange(createdAt, fromDate, toDate)

      return passCity && passBusiness && passDate
    })
  }, [codOrders, cityFilter, businessFilter, fromDate, toDate])

  // ---- Init ----
  useEffect(() => {
    getAllCodLeads()
    getAllBusinesses()
  }, []) // load once

  // ---- Modal ----
  const toggleModal = (lead = null) => {
    setSelectedLead(lead)
    setModal(m => !m)
  }

  const columns = useMemo(
    () => [
      { Header: "Order ID", accessor: "order_id", disableFilters: true },

      {
        Header: "Business",
        accessor: row =>
          val(
            row.business?.name,
            row.business_name,
            row.abr_business_name,
            "-"
          ),
        id: "business_name",
        disableFilters: true,
      },
      {
        Header: "Pick Up",
        accessor: row => val(row.business?.origin, row.origin, "-"),
        id: "origin",
        disableFilters: true,
      },
      {
        Header: "Consignee",
        accessor: row => val(row.consignee?.name, row.consignee_name, "-"),
        id: "consignee_name",
        disableFilters: true,
      },
      {
        Header: "Drop Off",
        accessor: row => {
          const city = val(row.consignee?.city, row.consignee_city, "")
          const addr = val(
            row.consignee?.full_address,
            row.consignee_full_address,
            ""
          )
          const joined = city && addr ? `${city} / ${addr}` : city || addr
          return joined || "-"
        },
        id: "drop_off",
        disableFilters: true,
      },
      {
        Header: "Total",
        accessor: "total_collect",
        disableFilters: true,
        Cell: ({ value }) => <span>PKR {formatCurrency(value)}</span>,
      },
      {
        Header: "Status",
        accessor: row => val(row.lead_status, "New"),
        id: "lead_status",
        disableFilters: true,
        Cell: ({ value }) => {
          const raw = (value ?? "New").toString().trim()
          const lower = raw.toLowerCase()
          const isNew = lower === "new"
          const isCancelled = lower === "cancelled" || lower === "canceled"
          const badgeClass = isNew
            ? "bg-primary"
            : isCancelled
            ? "bg-danger"
            : "bg-secondary"
          return (
            <span className={`badge ${badgeClass}`}>{isNew ? "New" : raw}</span>
          )
        },
      },
      {
        Header: "Created",
        accessor: row =>
          val(row.lead_created_at, row.lead_createdAt, row.createdAt),
        id: "lead_created_at",
        disableFilters: true,
        Cell: ({ value }) => <span>{formatDateTime(value)}</span>,
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableFilters: true,
        Cell: ({ row }) => {
          const original = row.original
          const s = String(val(original.lead_status, "New")).toLowerCase()
          const isCancelled = s === "cancelled" || s === "canceled"
          const leadId = val(original.cod_lead_id, original.id)

          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => toggleModal(original)}
              >
                Detail
              </button>
              {isCancelled ? (
                <button className="btn btn-success btn-sm" disabled>
                  Process
                </button>
              ) : (
                <Link
                  className="btn btn-success btn-sm"
                  to={`/process_cod/${leadId}`}
                >
                  Process
                </Link>
              )}
            </div>
          )
        },
      },
    ],
    []
  )

  // ---- Reset filters ----
  const resetFilters = () => {
    setCityFilter("")
    setBusinessFilter("")
    setFromDate("")
    setToDate("")
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">COD Leads</h5>
            </Col>
            <Col xl="6" className="text-end">
              <Button
                color="primary"
                className="btn-sm w-md"
                onClick={openCreate}
              >
                <i className="fa fa-fw fa-plus me-1" />
                New COD Lead
              </Button>
            </Col>
          </Row>

          {/* Filters */}
          <Row className="pt-3 g-3 align-items-end">
            {/* <Col md="3">
              <Label htmlFor="cityFilter">City</Label>
              <Input
                id="cityFilter"
                type="text"
                placeholder="e.g. Lahore"
                value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
                list="cityOptions"
              />
              <datalist id="cityOptions">
                {citySuggestions.map(c => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </Col> */}

            <Col md="3">
              <Label htmlFor="businessFilter">Business</Label>
              <Input
                id="businessFilter"
                type="select"
                value={businessFilter}
                onChange={e => setBusinessFilter(e.target.value)}
              >
                <option value="">All approved businesses</option>
                {approvedBusinesses.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.businessName || b.name || `#${b.id}`}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md="2">
              <Label htmlFor="fromDate">From</Label>
              <Input
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </Col>

            <Col md="2">
              <Label htmlFor="toDate">To</Label>
              <Input
                id="toDate"
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </Col>

            <Col md="2" className="d-flex gap-2">
              <Button
                color="secondary"
                onClick={resetFilters}
                className="w-100"
              >
                Reset
              </Button>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  {isLoading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "200px" }}
                    >
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <div className="float-end d-flex gap-2">
                        <ExcelDownload leads={filteredRows} />
                      </div>
                      <TableContainer
                        columns={columns}
                        data={filteredRows}
                        isGlobalFilter={true}
                        customPageSize={10}
                        className="custom-table"
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        {/* Lead Details Modal */}
        <Modal isOpen={modal} toggle={() => toggleModal()} centered size="lg">
          <ModalHeader toggle={() => toggleModal()}>Lead Details</ModalHeader>

          <ModalBody>
            {selectedLead && (
              <div className="container-fluid">
                <div className="row g-3">
                  {/* Business */}
                  <div className="col-md-6">
                    <div className="card shadow-sm h-100">
                      <div className="card-header py-2">
                        <strong>Business</strong>
                      </div>
                      <div className="card-body">
                        <dl className="row mb-0">
                          <dt className="col-5">Name</dt>
                          <dd className="col-7">
                            {val(
                              selectedLead.business?.name,
                              selectedLead.business_name,
                              selectedLead.abr_business_name,
                              "-"
                            )}
                          </dd>
                          <dt className="col-5">Phone</dt>
                          <dd className="col-7">
                            {val(selectedLead.business?.phone, "-")}
                          </dd>
                          <dt className="col-5">Email</dt>
                          <dd className="col-7">
                            {val(selectedLead.business?.email, "-")}
                          </dd>

                          <dt className="col-5">Origin(PickUp)</dt>
                          <dd className="col-7">
                            {val(selectedLead.origin, "-")}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* Consignee */}
                  <div className="col-md-6">
                    <div className="card shadow-sm h-100">
                      <div className="card-header py-2">
                        <strong>Consignee</strong>
                      </div>
                      <div className="card-body">
                        <dl className="row mb-0">
                          <dt className="col-5">Name</dt>
                          <dd className="col-7">
                            {val(
                              selectedLead.consignee?.name,
                              selectedLead.consignee_name,
                              "-"
                            )}
                          </dd>

                          <dt className="col-5">City</dt>
                          <dd className="col-7">
                            {val(
                              selectedLead.consignee?.city,
                              selectedLead.consignee_city,
                              "-"
                            )}
                          </dd>

                          <dt className="col-5">Contact 1</dt>
                          <dd className="col-7">
                            {val(
                              selectedLead.consignee?.contact1,
                              selectedLead.consignee_contact1,
                              "-"
                            )}
                          </dd>

                          <dt className="col-5">Contact 2</dt>
                          <dd className="col-7">
                            {val(
                              selectedLead.consignee?.contact2,
                              selectedLead.consignee_contact2,
                              "-"
                            )}
                          </dd>

                          <dt className="col-5">Email</dt>
                          <dd className="col-7">
                            {val(
                              selectedLead.consignee?.email,
                              selectedLead.consignee_email,
                              "-"
                            )}
                          </dd>
                          <dt className="col-5">Address(DropOff)</dt>
                          <dd className="col-7">
                            {val(selectedLead.consignee?.full_address, "-")}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* Product */}
                  <div className="col-md-6">
                    <div className="card shadow-sm h-100">
                      <div className="card-header py-2">
                        <strong>Product</strong>
                      </div>
                      <div className="card-body">
                        <dl className="row mb-0">
                          <dt className="col-5">Order ID</dt>
                          <dd className="col-7">
                            {val(selectedLead.order_id, "-")}
                          </dd>

                          <dt className="col-5">Description</dt>
                          <dd className="col-7">
                            {val(selectedLead.product_desc, "-")}
                          </dd>

                          <dt className="col-5">Qty</dt>
                          <dd className="col-7">
                            {val(selectedLead.item_qty, "-")} x
                          </dd>

                          <dt className="col-5">Weight</dt>
                          <dd className="col-7">
                            {val(selectedLead.weight, "-")} kg
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="col-md-6">
                    <div className="card shadow-sm h-100">
                      <div className="card-header py-2">
                        <strong>Pricing</strong>
                      </div>
                      <div className="card-body">
                        <dl className="row mb-0">
                          <dt className="col-5">COD Amount</dt>
                          <dd className="col-7">
                            PKR {formatCurrency(selectedLead.cod_amount)}
                          </dd>

                          <dt className="col-5">Tax Amount</dt>
                          <dd className="col-7">
                            PKR {formatCurrency(selectedLead.tax_amount)}
                          </dd>

                          <dt className="col-5">Total Collect</dt>
                          <dd className="col-7">
                            PKR {formatCurrency(selectedLead.total_collect)}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* Lead Meta */}
                  <div className="col-12">
                    <div className="card shadow-sm">
                      <div className="card-header py-2">
                        <strong>Lead</strong>
                      </div>
                      <div className="card-body">
                        <dl className="row mb-0">
                          <dt className="col-md-3 col-5">Service Type</dt>
                          <dd className="col-md-9 col-7">
                            {val(selectedLead.service_type, "-")}
                          </dd>

                          <dt className="col-md-3 col-5">Unique ID</dt>
                          <dd className="col-md-9 col-7">
                            {val(selectedLead.cod_lead_uni_id, "-")}
                          </dd>

                          <dt className="col-md-3 col-5">Created</dt>
                          <dd className="col-md-9 col-7">
                            {formatDateTime(
                              val(
                                selectedLead.lead_created_at,
                                selectedLead.lead_createdAt,
                                selectedLead.createdAt
                              )
                            )}
                          </dd>

                          <dt className="col-md-3 col-5">Updated</dt>
                          <dd className="col-md-9 col-7">
                            {formatDateTime(
                              val(
                                selectedLead.lead_updated_at,
                                selectedLead.lead_updatedAt,
                                selectedLead.updatedAt
                              )
                            )}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={() => toggleModal()}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        <CreateCodLeadModal
          isOpen={createOpen}
          onClose={closeCreate}
          onCreated={getAllCodLeads}
          approvedBusinesses={approvedBusinesses}
          apiURl={apiURl}
        />
      </div>
    </React.Fragment>
  )
}

export default AllBusinessCodLeads
